import { Component, OnInit, OnDestroy } from '@angular/core';
import { UploadShortageFileService } from './upload-shortage-file.service';
import { ToastrService } from 'ngx-toastr';
import { AppConstants } from 'src/app/app.constants';

@Component({
  selector: 'app-upload-shortage-file',
  templateUrl: './upload-shortage-file.component.html',
  styleUrl: './upload-shortage-file.component.scss'
})
export class UploadShortageFileComponent implements OnInit, OnDestroy {
  selectedDate: Date | null = null;
  selectedFile: File | null = null;
  isUploading: boolean = false;
  isLoadingLogs: boolean = false;
  logs: any[] = [];
  userEmail: string = '';
  pollingInterval: any;
  currentFileId: string | null = null;
  isDestroyed: boolean = false;
  processedRecords: number = 0;
  totalRecords: number = 0;
  remainingRecords: number = 0;

  // Pagination
  first: number = 0;
  rows: number = 10;

  constructor(
    private shortageService: UploadShortageFileService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.userEmail = localStorage.getItem('email') || '';

    // Retrieve active file ID from localStorage to resume polling on refresh
    this.currentFileId = localStorage.getItem('currentFileId') || null;
    if (this.currentFileId) {
      this.isUploading = true;
      this.startPolling(this.currentFileId);
    }

    this.refreshLogs();
  }

  onPageChange(event: any): void {
    this.first = event.first;
    this.rows = event.rows;
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.stopPolling();
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.selectedFile = file;
    }
  }

  formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  uploadFile(): void {
    if (!this.selectedFile || !this.selectedDate) {
      this.toastr.warning('Please select both a file and a date.');
      return;
    }

    this.isUploading = true;
    const formattedDate = this.formatDate(this.selectedDate);

    this.shortageService.uploadShortageFile(this.selectedFile, formattedDate, this.userEmail).subscribe({
      next: (res) => {
        this.toastr.success(res.message || 'File uploaded successfully. Processing mapping...');

        // Save the file ID to currentFileId and localStorage to persist across refreshes
        const fileId = res.file_id || (res.data && res.data.file_id) || null;
        this.currentFileId = fileId;
        if (fileId) {
          localStorage.setItem('currentFileId', fileId);
          this.startPolling(fileId);
        }

        this.clearFilter();
        this.refreshLogs();
      },
      error: (err) => {
        this.isUploading = false;
        const errorMsg = err.error?.error || err.error?.message || 'Something went wrong';
        this.toastr.error(errorMsg);
      }
    });
  }

  clearFilter(): void {
    this.selectedFile = null;
    this.selectedDate = null;
    // Reset file input
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  }

  refreshLogs(): void {
    if (!this.userEmail) return;

    this.isLoadingLogs = true;
    this.shortageService.getShortageRecords(this.userEmail).subscribe({
      next: (res) => {
        this.isLoadingLogs = false;
        this.logs = res.data || [];

        // Disable upload for all users if any log record shows a file is still processing
        const processingLog = this.logs.find((log: any) => log.status === 'processing');
        if (processingLog) {
          this.isUploading = true;

          // Try to resolve the file ID to start/continue polling for this user session
          const fileId = this.currentFileId || processingLog.file_id || processingLog._id;
          if (fileId) {
            localStorage.setItem('currentFileId', fileId);
            this.currentFileId = fileId;
            this.startPolling(fileId);
          } else {
            console.warn('Latest log is processing but no file_id found to call process_nda_mapping');
          }
        } else {
          // Only enable upload if we are not actively polling (no currentFileId in localStorage)
          if (!this.currentFileId) {
            this.isUploading = false;
            this.stopPolling();
          }
        }
      },
      error: (err) => {
        this.isLoadingLogs = false;
        this.toastr.error('Failed to fetch upload logs');
      }
    });
  }

  startPolling(fileId: string): void {
    if (this.pollingInterval) return;

    this.processedRecords = 0;
    this.totalRecords = 0;
    this.remainingRecords = 0;

    const callApi = () => {
      this.shortageService.processNdaMapping(fileId, 100, this.userEmail).subscribe({
        next: (res) => {
          if (this.isDestroyed) return;
          this.processedRecords = res.processed_records || 0;
          this.totalRecords = res.total_records || 0;
          this.remainingRecords = res.remaining_records || 0;

          // Completion condition: status === 'completed' && remaining_records === 0
          if (res.status === 'completed' && this.remainingRecords === 0) {
            this.isUploading = false;
            this.stopPolling();
            localStorage.removeItem('currentFileId');
            this.currentFileId = null;
            this.toastr.success(res.message || 'File processing completed.');
            this.refreshLogs();
          }
        },
        error: (err) => {
          if (this.isDestroyed) return;
          this.isUploading = false;
          this.stopPolling();
          localStorage.removeItem('currentFileId');
          this.currentFileId = null;
          const errorMsg = err.error?.error || err.error?.message || 'Error processing file mapping';
          this.toastr.error(errorMsg);
          this.refreshLogs();
        }
      });
    };

    // Call once immediately
    callApi();

    this.pollingInterval = setInterval(callApi, 45000);
  }

  stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  getStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'completed': return 'badge bg-success';
      case 'failed': return 'badge bg-danger';
      case 'processing': return 'badge bg-warning text-dark';
      default: return 'badge bg-secondary';
    }
  }

  downloadFile(path: string): void {
    if (!path || path === '#') {
      this.toastr.info('File path not available.');
    } else {
      const fullUrl = path.startsWith('http') ? path : `${AppConstants.API_ENDPOINT1}${path}`;
      window.open(fullUrl, '_blank');
    }
  }
}